import { PropTypes } from 'prop-types';
import styled from 'styled-components';

import { Flex } from '../../grid';
import { ModalHeaderButton } from '../../modal-header-button';

/**
 * W8 — Surprise & Delight: ThresholdLost
 *
 * Blocking confirm modal shown when the cart transitions from
 * S&D-eligible to no-longer-eligible while a gift was selected.
 * Centered modal with dark backdrop on all viewports, per Figma.
 */
const ThresholdLost = ({ isOpen, title = 'Your order has been changed', message, ctaLabel = 'OKAY', onClose }) => {
  if (!isOpen) {
    return null;
  }

  return (
    <ModalBackdrop data-testid="threshold-lost-modal-backdrop">
      <ModalContent
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="threshold-lost-modal-title"
        data-testid="threshold-lost-modal"
      >
        <CloseButtonWrapper>
          <ModalHeaderButton
            onClick={onClose}
            icon="cross"
            ariaLabel="Close"
            data-testid="threshold-lost-modal-close"
          />
        </CloseButtonWrapper>
        <Flex flexDirection="column" alignItems="center" textAlign="center" px="24px" py="24px">
          {title && <ModalTitle id="threshold-lost-modal-title">{title}</ModalTitle>}
          <ModalMessage>{message}</ModalMessage>
          <PrimaryRedButton type="button" onClick={onClose} data-testid="threshold-lost-modal-cta">
            {ctaLabel}
          </PrimaryRedButton>
        </Flex>
      </ModalContent>
    </ModalBackdrop>
  );
};

const ModalBackdrop = styled.div`
  align-items: center;
  background: rgba(0, 0, 0, 0.5);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  padding: 24px;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #f7f1ee; /* warmWhite100 */
  border-radius: 24px;
  max-width: 360px;
  position: relative;
  width: 100%;
`;

const CloseButtonWrapper = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 1;
`;

const ModalTitle = styled.h2`
  font-family: Barlow, sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
  color: #000000;
  margin: 0 0 8px;
`;

const ModalMessage = styled.p`
  font-family: Barlow, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #000000;
  margin: 0 0 24px;
  text-align: center;
`;

const PrimaryRedButton = styled.button`
  background-color: #e81313;
  color: #ffffff;
  border: 0;
  border-radius: 999px;
  height: 48px;
  width: 100%;
  font-family: Barlow, sans-serif;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 4px;
  text-transform: uppercase;
  cursor: pointer;
`;

ThresholdLost.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  title: PropTypes.string,
  message: PropTypes.string.isRequired,
  ctaLabel: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

ThresholdLost.defaultProps = {
  title: 'Your order has been changed',
  ctaLabel: 'OKAY',
};

export { ThresholdLost };
